import React, { useEffect, useMemo, useState } from 'react';
import { useTitle } from '../../Hooks';
import { Button, Header, Icon, Items, Message, Placeholder, Toast } from 'react-frontier';
import { bindClick } from '@tempu/components/Util';
import { BankAccount, SetLoading, UserBankAccount } from '../../Classes';
import { Modal } from '../../components';
import { firstBy } from 'thenby';
import API from '../../API';
import BankAccountModal from '../../components/BankAccountModal';

var AccountCard = (props: { account: UserBankAccount, onEdit?: (a: UserBankAccount)=>void })=>{
	if(!props.account){
		return <div className="tp placeholder card account">
			<Placeholder width={30} height={30} random={false} />
			<div className="left">
				<Placeholder height={24} width={120} />
				<Placeholder height={14} width={80} />
			</div>
			<div className="right">
				<Placeholder height={34} width={100} random={false} />
			</div>
		</div>
	}
	return <div className="tp card placeholder account">
		<Icon name={props.account.clabe ? 'landmark' : 'credit-card'} />
		<div className="left">
			{!!props.account.primary_account && (
				<div className="primary">Cuenta primaria</div>
			)}
			<div className="title">
				{props.account.clabe ? <>
					<b>SPEI</b> **{props.account.clabe.slice(-5)}
				</> : <>
					<b>{props.account.bank}</b> **{props.account.card_number.slice(-4)}
				</>}
			</div>
			<div className="name">{props.account.name_account}</div>
		</div>
		<div className="right">
			<Button size='small' text='Editar' iconName='pen' onClick={()=>{
				if(!props.onEdit) return;
				props.onEdit(props.account);
			}} />
		</div>
	</div>
}

var AccountBanks = ()=>{
	var { setTitle, setAction } = useTitle();
	var [loadError, setLoadError] = useState<string>(null);
	var [addModal, setAddModal] = useState<boolean>(false);
	var [primaryModal, setPrimaryModal] = useState<boolean>(false);
	var [selectedPrimary, setSelectedPrimary] = useState<number>(null);
	var [selectedAccount, setSelectedAccount] = useState<number>(null);
	var [accounts, setAccounts] = useState<UserBankAccount[]>(null);

	useEffect(()=>{
		setTitle('Cuentas bancarias');
		setAction((
			<Button text='Agregar cuenta' color='black' iconName='plus-circle' onClick={bindClick(setAddModal, true)} />
		));
		API.getUserBankAccounts().then(res=>{
			if(res.error) return setLoadError(res.message);
			return setAccounts(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error inesperado cargando los datos (LCL-ACBKAC-1)');
		});
	}, []);

	var onCreateAccount = (account: BankAccount)=>{
		setAddModal(false);
		Toast.success('Se ha agregado la cuenta bancaria a tu cuenta')
		var ac = [...accounts];
		if(account.primary_account){
			ac.forEach(a=>{
				a.primary_account = false
			});
		}
		ac.push(account);
		setAccounts(ac);
	}

	var onEditAccount = (account_id: number, account: BankAccount)=>{
		setAddModal(false);
		setSelectedAccount(null);
		Toast.success('Se ha editado la cuenta bancaria');
		var ac = [...accounts];
		var aix = ac.findIndex(a=>a.account_id===account_id);
		if(aix!=-1){
			if(account.primary_account){
				ac.forEach(a=>{
					a.primary_account = false
				});
			}
			ac[aix] = account;
			setAccounts(ac);
		}
	}
	
	var sendPrimary = (setLoading: SetLoading)=>{
		if(!selectedPrimary) return Toast.error('Favor de seleccionar una cuenta primaria');
		setLoading(true);

		API.setPrimaryAccount(selectedPrimary).then(res=>{
			if(res.error) return Toast.error(res.message);
			var acl = [...accounts];
			var acix = acl.findIndex(a=>a.account_id===selectedPrimary);
			if(acix!==-1){
				acl[acix].primary_account = true;
				setAccounts(acl);
			}
			setPrimaryModal(false);
			Toast.success('Se ha actualizado la cuenta primaria')
		}).catch(err=>{
			Toast.error('Hubo un error inesperado modificando la cuenta primaria (LCL-BKPACC-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var has_primary = useMemo(()=>{
		if(!accounts) return true;
		return accounts.findIndex(a=>!!a.primary_account)!=-1;
	}, [accounts]);

	var sorted_accounts = useMemo(()=>{
		if(!accounts) return null;
		return accounts.sort(firstBy('primary_account', 'desc'));
	}, [accounts]);

	if(loadError) return <Header text={'Error'} subtext={loadError} iconName='face-frown-open' />
	
	return <div className="tp bank-accounts-screen">
		{!!accounts && accounts.length==0 && (
			<Header 
				iconName='landmark' 
				text={'Sin cuentas bancarias'}
				subtext={'Agrega una cuenta bancaria para poder depositarte tu saldo a favor.'} 
				actions={(
					<Button text='Agregar cuenta' color='black' iconName='plus-circle' onClick={bindClick(setAddModal, true)} />
				)}
			/>
		)}
		{!has_primary && (
			<Message style={{ marginBottom: 15 }} header='Sin cuenta primaria' centered>
				Es recomendable seleccionar una cuenta bancaria primaria. Esto hará que la cuenta que selecciones como cuenta primaria se usará para enviarte tu dinero en cuanto lo recibas.
				<br />
				<Button text='Seleccionar cuenta primaria' color='black' style={{ marginTop: 10 }} onClick={bindClick(setPrimaryModal, true)} />
			</Message>
		)}
		{accounts ? (
			accounts.map(a=>(
				<AccountCard account={a} key={`BKACC-${a.account_id}`} onEdit={b=>{
					setSelectedAccount(b.account_id);
				}} />
			))
		) : (
			new Array(3).fill(0).map((a,i)=>(
				<AccountCard account={null} key={`PLHAC-${i}`} />
			))
		)}
		<BankAccountModal
			open={addModal || !!selectedAccount}
			onClose={bindClick(()=>{
				setSelectedAccount(null);
				setAddModal(false);
			}, false)}
			account_id={selectedAccount}
			onCreate={onCreateAccount}
			onEdit={onEditAccount}
		/>
		<Modal open={primaryModal} onClose={bindClick(setPrimaryModal)} size='tiny'>
			<Modal.Header>Seleccionar cuenta primaria</Modal.Header>
			<Modal.Content>
				<Items
					toggle
					single
					selectable
					data={sorted_accounts}
					selected={selectedPrimary}
					valueExtractor={(i)=>i.account_id}
					onToggle={(v)=>setSelectedPrimary(v)}
					render={a=>(
						<div className='data' style={{ display: 'flex', alignItems: 'center' }}>
							<Icon name={a.clabe ? 'landmark' : 'credit-card'} />
							<div className="info">
								<b>{!a.clabe ? a.bank : 'SPEI'}</b> **{a.clabe ? a.clabe.slice(-5) : a.card_number.slice(-4)}
								<div className="number" style={{ color: 'gray', fontSize: 12, lineHeight: '12px' }}>
									{a.name_account}
								</div>
							</div>
						</div>
					)}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setPrimaryModal, false)} />
				<Button text='Guardar' color='black' onClick={sendPrimary} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default AccountBanks;